import { useCallback, useEffect, useRef, useState } from "react";
import { theme, btnPrimary, inputStyle, type as text } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { fmtDateOnly } from "../lib/dateOnly";
import { fmtDateTime } from "../lib/format";
import { StatusTag } from "./Drawing";
import { DetailSection } from "./RecordView";
import { secondaryBtn } from "../pages/resilience/shared";

// "Read from the signed copy" on an agreement's view in the supplier panel's Contracts tab (docs/TPRM-CONTRACTS.md
// "Reading the signed copy"). The member links the signed agreement from the Evidence Repository (or uploads it
// there), the platform reads it (GET / POST /tprm/suppliers/:id/contracts/:contractId/reading, lib/contractReading.ts)
// and proposes each field with the words of the agreement it rests on. Nothing reaches the agreement until the
// member accepts, edits or rejects each proposal. A term the reading could not find is never proposed, so it stays
// "not answered" on the agreement.

type Value = string | boolean | number | string[] | { timeHours: number; pointHours: number | null } | null;

interface Proposal {
  id: string;
  field: string;
  label: string;
  value: Value;
  current: Value;
  quote: string;
  source: "rule" | "ai";
  note: string | null;
  status: "pending" | "accepted" | "edited" | "rejected" | "superseded";
  decidedValue: Value;
  decidedAt: string | null;
}

interface ReadingView {
  signedCopy: { documentId: string; fileName: string; version: number; uploadedAt: string; readable: boolean } | null;
  reading: {
    id: string; documentVersion: number; fileName: string; status: "queued" | "running" | "done" | "error"; error: string | null;
    aiUsed: boolean; aiNote: string | null; truncated: boolean; matched: number; dropped: number; createdAt: string; completedAt: string | null;
  } | null;
  newerVersion: boolean;
  aiAvailable: boolean;
  proposals: Proposal[];
}

interface Candidate { id: string; fileName: string; version: number; uploadedAt: string }

const smallBtn: React.CSSProperties = { ...secondaryBtn, padding: "4px 10px", fontSize: 12 };
const READABLE = ".pdf,.docx,.txt,.md";

function describe(field: string, v: Value): string {
  if (v == null) return "Not recorded";
  if (field.startsWith("term:")) return v === true ? "In the agreement" : "Not in the agreement";
  switch (field) {
    case "startDate": case "endDate": return fmtDateOnly(`${v as string}T00:00:00.000Z`);
    case "autoRenew": return v ? "Yes, unless notice is given" : "No";
    case "noticePeriodDays": return `${v} days`;
    case "incidentNotificationHours": return `${v} hours`;
    case "dataLocations": return (v as string[]).join(", ");
    case "restoreCommitment": {
      const r = v as { timeHours: number; pointHours: number | null };
      return `Within ${r.timeHours} hours${r.pointHours != null ? `, losing at most ${r.pointHours} hours of data` : ""}`;
    }
    default: return String(v);
  }
}

/** The edit box for a field, holding its value as text; `parse` turns it back into the value the API takes. */
function editorFor(field: string) {
  if (field.startsWith("term:")) return { kind: "select" as const, options: [["true", "In the agreement"], ["false", "Not in the agreement"]], parse: (s: string) => s === "true" };
  switch (field) {
    case "startDate": case "endDate": return { kind: "date" as const, parse: (s: string) => s };
    case "autoRenew": return { kind: "select" as const, options: [["true", "Yes"], ["false", "No"]], parse: (s: string) => s === "true" };
    case "noticePeriodDays": case "incidentNotificationHours": return { kind: "number" as const, parse: (s: string) => (s === "" ? null : Number(s)) };
    case "dataLocations": return { kind: "text" as const, parse: (s: string) => s.split(",").map((x) => x.trim()).filter(Boolean) };
    default: return { kind: "restore" as const, parse: (s: string) => { const [t, p] = s.split("|"); return { timeHours: Number(t), pointHours: p === "" || p == null ? null : Number(p) }; } };
  }
}

function asText(field: string, v: Value): string {
  if (v == null) return "";
  if (field === "dataLocations") return (v as string[]).join(", ");
  if (field === "restoreCommitment") { const r = v as { timeHours: number; pointHours: number | null }; return `${r.timeHours}|${r.pointHours ?? ""}`; }
  return String(v);
}

const STATUS_LABELS: Record<Proposal["status"], string> = {
  pending: "To confirm", accepted: "Accepted", edited: "Edited and recorded", rejected: "Rejected", superseded: "Replaced by a newer reading",
};

export function ContractReadingPanel({ supplierId, contractId, canWrite, onChanged }: {
  supplierId: string;
  contractId: string;
  canWrite: boolean;
  onChanged?: () => void;
}) {
  const base = `/tprm/suppliers/${supplierId}/contracts/${contractId}`;
  const [view, setView] = useState<ReadingView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [choosing, setChoosing] = useState(false);
  const [candidates, setCandidates] = useState<Candidate[] | null>(null);
  const [picked, setPicked] = useState("");
  const [editing, setEditing] = useState<{ id: string; value: string } | null>(null);
  const [showDecided, setShowDecided] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try { setView(await api.get<ReadingView>(`${base}/reading`)); }
    catch (e) { setError(apiErrorMessage(e, "Could not load the reading of the signed copy")); }
  }, [base]);

  useEffect(() => { void load(); }, [load]);

  // While a reading runs, look again every few seconds.
  const running = view?.reading?.status === "queued" || view?.reading?.status === "running";
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => { void load(); }, 3000);
    return () => clearInterval(t);
  }, [running, load]);
  const wasRunning = useRef(false);
  useEffect(() => {
    if (wasRunning.current && !running) onChanged?.();
    wasRunning.current = running;
  }, [running, onChanged]);

  async function openChooser() {
    setChoosing(true); setError(null);
    try { setCandidates((await api.get<{ documents: Candidate[] }>("/tprm/signed-copy-candidates")).documents); }
    catch (e) { setError(apiErrorMessage(e, "Could not list the Evidence Repository")); }
  }

  async function link(documentId: string | null) {
    setBusy(true); setError(null);
    try { setView(await api.put<ReadingView>(`${base}/signed-copy`, { documentId })); setChoosing(false); setPicked(""); }
    catch (e) { setError(apiErrorMessage(e, "Could not link the signed copy")); }
    finally { setBusy(false); }
  }

  // Upload into the Evidence Repository (the ordinary evidence upload: lib/uploadPolicy.ts decides what is allowed), then link it.
  async function upload(file: File) {
    setBusy(true); setError(null);
    try {
      const contentType = file.type || "application/octet-stream";
      const { uploadUrl, blobPath } = await api.post<{ uploadUrl: string; blobPath: string }>("/documents/request-upload", { fileName: file.name, contentType });
      const put = await fetch(uploadUrl, { method: "PUT", headers: { "x-ms-blob-type": "BlockBlob", "Content-Type": contentType }, body: file }).catch(() => null);
      if (!put?.ok) throw new Error("Upload to storage failed — check your connection and try again.");
      const { document } = await api.post<{ document: { id: string } }>("/documents", { blobPath, fileName: file.name, sizeBytes: file.size, tags: [] });
      setView(await api.put<ReadingView>(`${base}/signed-copy`, { documentId: document.id }));
      setChoosing(false);
    } catch (e) { setError(apiErrorMessage(e, "Could not upload the signed copy")); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  }

  async function read() {
    setBusy(true); setError(null);
    try { setView(await api.post<ReadingView>(`${base}/reading`, {})); }
    catch (e) { setError(apiErrorMessage(e, "Could not start reading the agreement")); }
    finally { setBusy(false); }
  }

  async function decide(p: Proposal, decision: "accept" | "edit" | "reject", value?: unknown) {
    setBusy(true); setError(null);
    try {
      const r = await api.post<{ view: ReadingView }>(`${base}/proposals/${p.id}`, decision === "edit" ? { decision, value } : { decision });
      setView(r.view); setEditing(null);
      if (decision !== "reject") onChanged?.();
    } catch (e) { setError(apiErrorMessage(e, "Could not record that")); }
    finally { setBusy(false); }
  }

  if (!view) return error ? <div style={{ ...text.hint, color: "var(--c-dangerText)", marginBottom: 12 }}>{error}</div> : null;

  const pending = view.proposals.filter((p) => p.status === "pending");
  const decided = view.proposals.filter((p) => p.status !== "pending");
  const r = view.reading;
  const readingCurrent = !!(r && !view.newerVersion && r.status !== "error");

  return (
    <DetailSection
      title="Read from the signed copy"
      aside={pending.length > 0 ? <span style={{ ...text.hint, color: "var(--c-warningText)" }}>{pending.length} to confirm</span> : undefined}
    >
      <div style={{ ...text.hint, marginBottom: 10, lineHeight: 1.5 }}>
        The platform reads the signed agreement and proposes its dates, renewal, notice period, incident window, data locations, restore commitment and the terms below, each with the words it rests on.
        Nothing is recorded until you confirm it. A term the reading could not find stays not answered.
      </div>

      {/* The signed copy */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: theme.color.heading }}>Signed copy:</span>
        {view.signedCopy
          ? <span style={{ fontSize: 12, color: theme.color.text }}>{view.signedCopy.fileName} · version {view.signedCopy.version} · filed {fmtDateOnly(view.signedCopy.uploadedAt)}</span>
          : <span style={{ fontSize: 12, color: theme.color.muted }}>None linked</span>}
        {canWrite && !choosing && <button type="button" style={smallBtn} onClick={openChooser}>{view.signedCopy ? "Change" : "Link the signed copy"}</button>}
        {canWrite && view.signedCopy && !choosing && <button type="button" style={smallBtn} disabled={busy} onClick={() => link(null)}>Unlink</button>}
      </div>

      {choosing && (
        <div style={{ border: "1px solid var(--c-border)", borderRadius: 3, padding: 10, marginBottom: 10, display: "grid", gap: 8 }}>
          <label style={{ fontSize: 12, display: "grid", gap: 4 }}>
            From the Evidence Repository (PDF, Word or plain text)
            <select value={picked} onChange={(e) => setPicked(e.target.value)} style={{ ...inputStyle, fontSize: 13 }}>
              <option value="">{candidates == null ? "Loading…" : candidates.length ? "Choose a document" : "No readable documents on file"}</option>
              {(candidates ?? []).map((c) => <option key={c.id} value={c.id}>{c.fileName} (v{c.version}, {fmtDateOnly(c.uploadedAt)})</option>)}
            </select>
          </label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <button type="button" style={{ ...btnPrimary, padding: "5px 12px", fontSize: 12 }} disabled={!picked || busy} onClick={() => link(picked)}>Link this document</button>
            <span style={text.hint}>or</span>
            <button type="button" style={smallBtn} disabled={busy} onClick={() => fileRef.current?.click()}>Upload the signed copy</button>
            <input ref={fileRef} type="file" accept={READABLE} style={{ display: "none" }} onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }} />
            <button type="button" style={smallBtn} onClick={() => { setChoosing(false); setPicked(""); }}>Cancel</button>
          </div>
          <div style={text.hint}>An upload is filed in the Evidence Repository like any other document; a later version of it is read as the new signed copy.</div>
        </div>
      )}

      {/* The reading */}
      {view.signedCopy && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
          {canWrite && !readingCurrent && !running && (
            <button type="button" style={{ ...btnPrimary, padding: "6px 14px", fontSize: 12 }} disabled={busy || !view.signedCopy.readable} onClick={read}>
              {r && view.newerVersion ? `Read version ${view.signedCopy.version}` : r?.status === "error" ? "Read it again" : "Read the agreement"}
            </button>
          )}
          {r && (
            <span style={{ ...text.hint, color: r.status === "error" ? "var(--c-dangerText)" : theme.color.muted }}>
              {r.status === "queued" || r.status === "running" ? `Reading version ${r.documentVersion}…`
                : r.status === "error" ? (r.error ?? "The reading failed.")
                : `Version ${r.documentVersion} read ${r.completedAt ? fmtDateTime(r.completedAt) : ""}${r.aiUsed ? ", the clauses by AI" : ""}.${r.matched ? ` ${r.matched} value${r.matched === 1 ? " was" : "s were"} already recorded.` : ""}${r.dropped ? ` ${r.dropped} AI reading${r.dropped === 1 ? " was" : "s were"} left out because the quoted words are not in the agreement.` : ""}`}
            </span>
          )}
          {view.newerVersion && r && <span style={{ ...text.hint, color: "var(--c-warningText)" }}>A newer version of the signed copy is on file.</span>}
        </div>
      )}
      {r?.status === "done" && r.aiNote && <div style={{ ...text.hint, color: "var(--c-warningText)", marginBottom: 10 }}>{r.aiNote}</div>}
      {r?.status === "done" && r.truncated && <div style={{ ...text.hint, color: "var(--c-warningText)", marginBottom: 10 }}>The agreement is long; only its start was read.</div>}
      {!view.aiAvailable && view.signedCopy && !r && <div style={{ ...text.hint, marginBottom: 10 }}>On this service tier the dates, renewal, notice period and incident window are read; the clauses are read by AI from Self-Service + AI.</div>}

      {/* Proposals */}
      {pending.length > 0 && (
        <div style={{ display: "grid", gap: 8, marginBottom: 8 }}>
          {pending.map((p) => {
            const ed = editorFor(p.field);
            const isEditing = editing?.id === p.id;
            return (
              <div key={p.id} style={{ border: "1px solid var(--c-border)", borderRadius: 3, padding: "8px 10px" }}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{p.label}</div>
                  <span style={{ ...text.hint }}>{p.source === "rule" ? "Read by rule" : "Read by AI"}</span>
                </div>
                <div style={{ fontSize: 13, color: theme.color.text, marginTop: 2 }}>
                  Proposed: <strong>{describe(p.field, p.value)}</strong>
                  <span style={{ color: theme.color.muted }}> · recorded now: {describe(p.field, p.current)}</span>
                </div>
                {p.note && <div style={{ ...text.hint, marginTop: 2 }}>{p.note}</div>}
                <blockquote style={{ margin: "6px 0", padding: "4px 10px", borderLeft: "3px solid var(--c-line)", fontSize: 12, color: theme.color.subtle, lineHeight: 1.5 }}>
                  {p.quote}
                </blockquote>
                {canWrite && !isEditing && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <button type="button" style={{ ...btnPrimary, padding: "4px 12px", fontSize: 12 }} disabled={busy} onClick={() => decide(p, "accept")}>Accept</button>
                    <button type="button" style={smallBtn} disabled={busy} onClick={() => setEditing({ id: p.id, value: asText(p.field, p.value) })}>Edit</button>
                    <button type="button" style={smallBtn} disabled={busy} onClick={() => decide(p, "reject")}>Reject</button>
                  </div>
                )}
                {canWrite && isEditing && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                    {ed.kind === "select" && (
                      <select value={editing.value} onChange={(e) => setEditing({ id: p.id, value: e.target.value })} style={{ ...inputStyle, fontSize: 13, width: "auto" }}>
                        {ed.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    )}
                    {ed.kind === "date" && <input type="date" value={editing.value} onChange={(e) => setEditing({ id: p.id, value: e.target.value })} style={{ ...inputStyle, fontSize: 13, width: "auto" }} />}
                    {ed.kind === "number" && <input type="number" min={0} value={editing.value} onChange={(e) => setEditing({ id: p.id, value: e.target.value })} style={{ ...inputStyle, fontSize: 13, width: 110 }} />}
                    {ed.kind === "text" && <input value={editing.value} onChange={(e) => setEditing({ id: p.id, value: e.target.value })} placeholder="Comma-separated" style={{ ...inputStyle, fontSize: 13, minWidth: 220 }} />}
                    {ed.kind === "restore" && (
                      <>
                        <label style={{ fontSize: 12 }}>Restore within (hours) <input type="number" min={0} value={editing.value.split("|")[0]} onChange={(e) => setEditing({ id: p.id, value: `${e.target.value}|${editing.value.split("|")[1] ?? ""}` })} style={{ ...inputStyle, fontSize: 13, width: 90 }} /></label>
                        <label style={{ fontSize: 12 }}>Data loss at most (hours) <input type="number" min={0} value={editing.value.split("|")[1] ?? ""} onChange={(e) => setEditing({ id: p.id, value: `${editing.value.split("|")[0]}|${e.target.value}` })} style={{ ...inputStyle, fontSize: 13, width: 90 }} /></label>
                      </>
                    )}
                    <button type="button" style={{ ...btnPrimary, padding: "4px 12px", fontSize: 12 }} disabled={busy} onClick={() => decide(p, "edit", ed.parse(editing.value))}>Record</button>
                    <button type="button" style={smallBtn} onClick={() => setEditing(null)}>Cancel</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {r?.status === "done" && pending.length === 0 && !view.newerVersion && (
        <div style={{ ...text.hint, marginBottom: 8 }}>Nothing left to confirm from this reading.</div>
      )}

      {decided.length > 0 && (
        <div>
          <button type="button" style={{ ...smallBtn, border: "none", paddingLeft: 0 }} onClick={() => setShowDecided((s) => !s)}>
            {showDecided ? "Hide" : "Show"} {decided.length} decided
          </button>
          {showDecided && (
            <ul style={{ margin: "4px 0 0", paddingLeft: 18, fontSize: 12, color: theme.color.text, lineHeight: 1.7 }}>
              {decided.map((p) => (
                <li key={p.id}>
                  <StatusTag tone={p.status === "accepted" || p.status === "edited" ? "met" : "none"}>{STATUS_LABELS[p.status]}</StatusTag>{" "}
                  {p.label}: {describe(p.field, p.status === "edited" ? p.decidedValue : p.value)}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {error && <div style={{ ...text.hint, color: "var(--c-dangerText)", marginTop: 8 }}>{error}</div>}
    </DetailSection>
  );
}
